import MachineReportListView from '../components/machines/machine_reports/readMachineReports/machineReportListView';
import ReportPageHeader from '../components/machines/machine_reports/readMachineReports/reportPageHeader';
import MachineReportFiltration from '../components/machines/machine_reports/machineReportFiltration';
import { useState, useCallback, useEffect } from 'react';
import { CustomErrorLogger } from '../utils/CustomErrorLogger';
import { Divider, Box, LinearProgress } from '@mui/material';
import { isObjectEmpty } from '../utils/isObjectEmpty';
import {
    ListNavigationProvider,
    useListPageNavigation,
} from '../hooks/listNavigation';
import NoRecord from '../components/NoRecord';
import Page from '../components/Page';
import axios from 'axios';

const Reports = () => {


    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);
    const [totalReports, setTotalReports] = useState('');
    const [reportsFetched, setReportsFetched] = useState(0);
    const [filteredReportsCount, setFilteredReportsCount] = useState(0);
    const [machineReportFiltration, setMachineReportFiltration] = useState(
        JSON.parse(localStorage.getItem('reportFiltration')) ||
        {
            company_id: '',
            acompagnist_id: '',
            machine_id: '',
            search: '',
            date: '',
            status: '',
        });

    const defaultSortBy = {
        report_number: null,
        created_at: 'desc',
        visit_date: null,
        controle_date: null,
        company: null,
        accompanist: null,
        status: null,
        machine: null,
    };

    const [sortBy, setSortBy] = useState(() => {
        const storedSortBy = JSON.parse(localStorage.getItem('reportSortBy')) || {};
        return { ...defaultSortBy, ...storedSortBy }; // Merge defaults with stored sortBy
    });

    const { scrollRef, offset, setOffset, navigationValue } = useListPageNavigation({
        id: 'reports',
        pathname: '/dashboard/controles',
        displayMode: 'list',
        itemSizes: { list: 70 },
        resetOnFilters: machineReportFiltration,
        resetOnSort: sortBy,
        ready: !loading,
    });
    const limit = 30;

    const filtersEmpty = isObjectEmpty(machineReportFiltration);

    useEffect(() => {
        localStorage.setItem('reportFiltration', JSON.stringify(machineReportFiltration));
    }, [machineReportFiltration]);

    useEffect(() => {
        localStorage.setItem('reportSortBy', JSON.stringify(sortBy));
    }, [sortBy]);

    useEffect(() => {
        setLoading(true)
    }, [machineReportFiltration]);

    const fetchReports = useCallback(async () => {
        try {
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('machine-control', { params: { offset: adjustedOffset, limit, filters: machineReportFiltration, sortBy } });
            if (response.status === 200 || response.status === 201) {
                // Create a copy of the current files array
                const updatedReports = [];
                // Update the fetched records in their correct positions
                response?.data?.controlesData?.forEach((file, index) => { updatedReports[adjustedOffset + index] = file; });
                // Update the state with the new files array
                setLoading(false);
                setReports(updatedReports);
                setFilteredReportsCount(response?.data?.filteredControlesCount);
                setTotalReports(response?.data?.totalControlesCount);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error Fetching reports:', error);
            CustomErrorLogger(error, 'fetch reports in reports page');
        }
    }, [offset, machineReportFiltration, sortBy]);

    useEffect(() => {
        fetchReports()
    }, [fetchReports]);

    const handleSort = (field) => {
        setSortBy((prevSortBy) => {
            const nextSortOrder = prevSortBy[field] === null ? 'asc'
                : prevSortBy[field] === 'asc' ? 'desc'
                    : null;

            return Object.fromEntries(
                Object.keys(prevSortBy)?.map((key) => [key, key === field ? nextSortOrder : null])
            );
        });

    };


    const handleResetFilters = useCallback(() => {
        setMachineReportFiltration({
            search: '',
            company_id: '',
            date: '',
            isActive: false,
        });
    }, [setMachineReportFiltration])

    return (
        <ListNavigationProvider value={navigationValue}>
        <Page title="Rapports de contrôle" list={true}>
            <ReportPageHeader />
            <Divider />
            {loading ?
                <Box sx={{ marginTop: '15px' }}>
                    <MachineReportFiltration
                        filtersEmpty={filtersEmpty}
                        machineReportFiltration={machineReportFiltration}
                        setMachineReportFiltration={setMachineReportFiltration}
                        totalReports={totalReports}
                        fetchedReports={reportsFetched}
                        filteredReportsCount={filteredReportsCount}
                        handleResetFilters={handleResetFilters}
                    />
                    <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#EA5E00' }, backgroundColor: '#A8ADB8' }} />
                </Box> :
                filtersEmpty && reports?.length === 0 ?
                    <NoRecord
                        description='Aucun rapport disponible pour cette machine'
                        buttonTitle='Ajouter un rapport'
                    /> :
                    <Box sx={{ marginTop: '15px' }}>
                        <MachineReportFiltration
                            filtersEmpty={filtersEmpty}
                            machineReportFiltration={machineReportFiltration}
                            setMachineReportFiltration={setMachineReportFiltration}
                            totalReports={totalReports}
                            fetchedReports={reportsFetched}
                            filteredReportsCount={filteredReportsCount}
                            handleResetFilters={handleResetFilters}
                        />
                        <MachineReportListView
                            scrollRef={scrollRef}
                            setOffset={setOffset}
                            machineReportFiltration={machineReportFiltration}
                            setMachineReportFiltration={setMachineReportFiltration}
                            filteredReportsCount={filteredReportsCount}
                            setReportsFetched={setReportsFetched}
                            reports={reports}
                            sortBy={sortBy}
                            handleSort={handleSort}
                        />
                    </Box>
            }
        </Page>
        </ListNavigationProvider>
    );
};

export default Reports;